import * as React from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { Lock, ArrowRight, RotateCcw, MapPin, Package } from 'lucide-react';

export type StockStatType =
  | 'sealed-warehouse'
  | 'handed-out'
  | 'returned'
  | 'at-item-location';

interface StockStatCardProps {
  type: StockStatType;
  value: number | null;
  className?: string;
}

const statConfig: Record<
  StockStatType,
  {
    label: string;
    icon1: React.ComponentType<{ className?: string; size?: number }>;
    icon2: React.ComponentType<{ className?: string; size?: number }>;
    bgColor?: string;
    borderColor?: string;
    textColor?: string;
    iconColor?: string;
  }
> = {
  'sealed-warehouse': {
    label: 'SEALED WAREHOUSE',
    icon1: Lock,
    icon2: Package,
  },
  'handed-out': {
    label: 'HANDED OUT',
    icon1: ArrowRight,
    icon2: Package,
  },
  returned: {
    label: 'RETURNED',
    icon1: RotateCcw,
    icon2: Package,
  },
  'at-item-location': {
    label: 'AT ITEM LOCATION',
    icon1: MapPin,
    icon2: Package,
    bgColor: 'tw:bg-yellow-500',
    borderColor: 'tw:border-yellow-400',
    textColor: 'tw:text-white',
    iconColor: 'tw:text-white',
  },
};

export function StockStatCard({ type, value, className }: StockStatCardProps) {
  const config = statConfig[type];
  const isHighlighted = type === 'at-item-location';

  return (
    <Card
      className={cn(
        'tw:flex tw:flex-col tw:gap-1.5 tw:items-center tw:justify-center tw:min-h-[96px] tw:p-4',
        className,
        isHighlighted ? config.bgColor : 'tw:bg-white tw:border-neutral-200',
        isHighlighted ? config.borderColor : 'tw:border-neutral-200',
      )}
    >
      <div
        className={cn(
          'tw:text-[10px] tw:font-medium tw:leading-normal tw:text-center tw:uppercase',
          isHighlighted ? config.textColor : 'tw:text-neutral-500',
        )}
      >
        {config.label}
      </div>
      <div className="tw:flex tw:gap-2 tw:items-center">
        <config.icon1
          className={cn(
            'tw:size-[26px]',
            isHighlighted ? config.iconColor : 'tw:text-neutral-900',
          )}
        />
        <config.icon2
          className={cn(
            'tw:size-[26px]',
            isHighlighted ? config.iconColor : 'tw:text-neutral-900',
          )}
        />
      </div>
      <div
        className={cn(
          'tw:text-[30px] tw:font-semibold tw:leading-none tw:tracking-[-0.3px] tw:text-center',
          isHighlighted ? config.textColor : 'tw:text-neutral-900',
        )}
      >
        {value === null ? '...' : value.toLocaleString()}
      </div>
    </Card>
  );
}
